import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkTable } from '@angular/cdk/table';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ITealiumAnalyticsOptions, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { SortDirection } from '@ddt-web/common';
import { CellData } from '@ddt-web/data-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest } from 'rxjs';

import { TableSearchService, TableSortService } from '../../services';
import { TableDataUtil } from '../../utils';

/**
 * Used as:
 * <app-table [dataArray]="tableData"></app-table>
 * or within other flags:
 * <app-table [dataArray]="tableData" [displayedColumns]="['ticker', 'name', 'as of date']" [isRowExpandable]="true" [isSortable]="true" [stickyColumnIndex]="[0]"></app-table>
 */
@UntilDestroy()
@Component({
	selector: 'ac-sticky-table-ie',
	templateUrl: './sticky-table-ie.component.html',
	styleUrls: ['./sticky-table-ie.component.scss'],
	animations: [
		trigger('detailExpand', [
			state('collapsed, void', style({ height: '0px', minHeight: '0' })),
			state('expanded', style({ height: '*' })),
			transition('expanded <=> collapsed, void <=> expanded', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
		])
	]
})
export class StickyTableIEComponent implements OnChanges, OnInit, AfterViewInit {
	@Input() public isSortable?: boolean = true;
	@Input() public stickyColumnIndex?: number[] = [0];
	@Input() public dataArray: Array<{ [key: string]: CellData }>;
	@Input() public displayedColumns?: string[];
	@Input() public columnsAlwaysToShow?: string[] = [];
	@Input() public showInfoBubble?: boolean = true;
	@Input() public showPagination?: boolean = false;
	@Input() public defaultSortKey?: string;
	@Input() public defaultSortDirection: number = 1;
	@Input() public searchableColumns?: string[];
	@Input() public infoBubbleMappings?: Array<{ [key: string]: string }>;
	@Input() public showSearchBar?: boolean = true;
	@Input() public showColumnRenderer?: boolean = true;
	// TODO: This feature is still in progress
	@Input() public isRowExpandable?: boolean = false;

	@ViewChild('horizontalScrollingTable') private horizontalScrollingTable: CdkTable<any>;
	@ViewChild('scrollingTable') private scrollingTable: CdkTable<any>;
	@ViewChild('verticalScrollingTable') private verticalScrollingTable: CdkTable<any>;
	@ViewChild('dummyVerticalScrolling') private dummyVerticalScrolling: ElementRef<any>;
	@ViewChild('dummyVerticalScrollingContent') private dummyVerticalScrollingContent: ElementRef<any>;
	@ViewChild('dummyHorizontalScrolling') private dummyHorizontalScrolling: ElementRef<any>;
	@ViewChild('dummyHorizontalScrollingContent') private dummyHorizontalScrollingContent: ElementRef<any>;
	@ViewChild('tableContainer') private tableContainer: ElementRef<any>;

	public tableDataSource$ = new BehaviorSubject<any[]>([]);
	public currentPage$ = new BehaviorSubject<number>(1);
	public pageSize$ = new BehaviorSubject<number>(0);
	public dataOnPage$ = new BehaviorSubject<any[]>([]);
	public searchFormControl = new UntypedFormControl();
	public sortKey$ = new BehaviorSubject<string>('');
	public sortDirection$ = new BehaviorSubject<number>(0);
	public expandedElement;
	public columnsObject;
	public showiSharesIdeasInfoBubbleModal: boolean = false;
	public iSharesIdeasInfoBubbleText: string;

	constructor(
		private tableSearchService: TableSearchService,
		private tableSortService: TableSortService,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	public ngOnInit(): void {
		combineLatest(this.tableDataSource$, this.currentPage$, this.pageSize$)
			.pipe(untilDestroyed(this))
			.subscribe(([allSources, currentPage, pageSize]) => {
				const startingIndex = (currentPage - 1) * pageSize;
				const onPage = allSources.slice(startingIndex, startingIndex + pageSize);
				this.dataOnPage$.next(onPage);
			});
		combineLatest(this.searchFormControl.valueChanges, this.sortKey$, this.sortDirection$)
			.pipe(untilDestroyed(this))
			.subscribe(([searchTerm, sortKey, sortDirection]) => {
				const filteredData = this.tableSearchService.searchDataArray(this.dataArray, this.searchableColumns, searchTerm);
				let sortedData = this.tableSortService.sortDataArray(filteredData, sortKey, sortDirection);
				sortedData = TableDataUtil.addColumnSeparators([], sortedData, this.displayedColumns);
				this.tableDataSource$.next(sortedData);
			});
		this.searchFormControl.setValue('');
	}

	public ngAfterViewInit(): void {
		this.calculateDummyScroll();
	}

	public ngOnChanges(): void {
		if (this.dataArray) {
			this.columnsObject = this.columnsObject || TableDataUtil.getColumnsGroupObject(this.dataArray);
			this.displayedColumns = this.displayedColumns || this.columnsAlwaysToShow.concat(TableDataUtil.getDisplayedColumns(this.columnsObject));
			this.pageSize$.next(this.dataArray.length);
			this.tableDataSource$.next(this.dataArray);
			if (this.defaultSortKey) {
				this.sortKey$.next(this.defaultSortKey);
			}
			if (this.defaultSortDirection) {
				this.sortDirection$.next(this.defaultSortDirection);
			}
		}
	}

	public adjustSort(key: string): void {
		if (this.sortKey$.value === key) {
			if (this.sortDirection$.value === SortDirection.ASC) {
				this.sortDirection$.next(SortDirection.DESC);
			} else {
				this.sortDirection$.next(SortDirection.ASC);
			}
			return;
		}
		this.sortKey$.next(key);
		this.sortDirection$.next(SortDirection.ASC);
	}

	public expandRow(element): void {
		if (this.isRowExpandable) {
			this.expandedElement = this.expandedElement === element ? null : element;
		}
	}

	public changeColumns(columns: string[]): void {
		this.displayedColumns = this.columnsAlwaysToShow.concat(columns);
		setTimeout(() => {
			this.calculateDummyScroll();
		}, 0);
		this.dataArray = TableDataUtil.clearColumnSeparators(this.displayedColumns, this.dataArray);
		this.tableDataSource$.next(TableDataUtil.addColumnSeparators([], this.dataArray, this.displayedColumns));
	}

	public setColumnObject(columnsObject: { [key: string]: { [key: string]: string | Object } }): void {
		this.columnsObject = columnsObject;
	}

	public updateScroll(scrollValue?: number): void {
		const scrollingTable = this.scrollingTable['_elementRef'].nativeElement as HTMLElement;
		const horizontalScrollingTable = this.horizontalScrollingTable['_elementRef'].nativeElement as HTMLElement;
		const verticalScrollingTable = this.verticalScrollingTable['_elementRef'].nativeElement as HTMLElement;
		const verticalScroller = this.dummyVerticalScrolling.nativeElement as HTMLElement;
		const horizontalScroller = this.dummyHorizontalScrolling.nativeElement as HTMLElement;

		if (scrollValue) { verticalScroller.scrollTop += scrollValue; }
		scrollingTable.scrollTop = verticalScroller.scrollTop;
		verticalScrollingTable.scrollTop = verticalScroller.scrollTop;

		scrollingTable.scrollLeft = horizontalScroller.scrollLeft;
		horizontalScrollingTable.scrollLeft = horizontalScroller.scrollLeft;
	}

	private calculateDummyScroll(): void {
		const scrollingTable = this.scrollingTable['_elementRef'].nativeElement as HTMLElement;
		const dummyVerticalContent = this.dummyVerticalScrollingContent.nativeElement as HTMLElement;
		const dummyHorizontalContent = this.dummyHorizontalScrollingContent.nativeElement as HTMLElement;

		dummyVerticalContent.style.height = scrollingTable.scrollHeight.toString() + 'px';
		dummyHorizontalContent.style.width = scrollingTable.scrollWidth.toString() + 'px';
	}

	@HostListener('mousewheel', ['$event'])
	public onMouseWheel(event): void {
		if (this.tableContainer.nativeElement.contains(event.target)) {
			const delta = Math.max(-1, Math.min(1, event.wheelDelta || -event.detail));
			this.updateScroll(-delta * 45);
			event.returnValue = false;
			if (event.preventDefault) {
				event.preventDefault();
			}
		}
	}

	/**
	 * Sets the text of the iShares Ideas modal and opens it.
	 *
	 * @param {string} trackId id used to track the event
	 * @param {*} infoBubble infoBubble text.
	 */
	public toggleInfoDialog(trackId: string, infoBubble: any): void {
		const analyticData: ITealiumAnalyticsOptions = { linkName: 'tooltip_' + trackId };
		const event = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.TOOL_TIP, analyticData);
		this.tealiumAnalyticsService.trackEventTealium(event);

		this.iSharesIdeasInfoBubbleText = infoBubble;
		this.showiSharesIdeasInfoBubbleModal = !this.showiSharesIdeasInfoBubbleModal;
	}
}
